import { useId, type ReactNode } from 'react';

interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  hint?: string;
}

export function FormField({ label, htmlFor, required, error, children, hint }: FormFieldProps) {
  const generatedId = useId();
  const id = htmlFor || generatedId;

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export function TextInput({ label, error, hint, className = '', ...props }: TextInputProps) {
  const generatedId = useId();
  const id = props.id || generatedId;

  return (
    <FormField label={label} htmlFor={id} required={props.required} error={error} hint={hint}>
      <input
        id={id}
        {...props}
        className={`w-full px-3 py-2 rounded border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : ''} ${className}`}
      />
    </FormField>
  );
}

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

export function TextArea({ label, error, hint, className = '', ...props }: TextAreaProps) {
  const generatedId = useId();
  const id = props.id || generatedId;

  return (
    <FormField label={label} htmlFor={id} required={props.required} error={error} hint={hint}>
      <textarea
        id={id}
        {...props}
        className={`w-full px-3 py-2 rounded border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : ''} ${className}`}
      />
    </FormField>
  );
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
}

export function Select({ label, error, hint, options, className = '', ...props }: SelectProps) {
  const generatedId = useId();
  const id = props.id || generatedId;

  return (
    <FormField label={label} htmlFor={id} required={props.required} error={error} hint={hint}>
      <select
        id={id}
        {...props}
        className={`w-full px-3 py-2 rounded border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : ''} ${className}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FormField>
  );
}
